/**
 * Home's lists: a tab bar with "View all" on its trailing end, over
 * the open tab's rows at the column's full width. Only that list renders, in
 * a box whose height comes from the window, never from the list, so no tab or
 * "Show more" moves the page; a list scrolls only when it is longer than the
 * box, and leaving a tab resets it.
 *
 * @module components/home/HomeTabBarLists
 */
import { Link } from "@tanstack/react-router";
import { ChevronRightIcon } from "lucide-react";
import { useState } from "react";

import { TIMELINE_ROW_ENTER_CLASS } from "../chat/timelineDisclosure";
import { ScrollArea, ScrollAreaContent } from "../ui/scroll-area";
import { TabBar } from "../ui/tab-bar";
import { TabsPanel } from "../ui/tabs";
import { HomeTabList, type HomeListsData } from "./HomeTabList";
import {
  defaultHomeTab,
  HOME_TAB_LABEL,
  HOME_TAB_VIEW_ALL,
  HOME_TABS,
  homeTabCountLabel,
  homeTabCounts,
  type HomeTab,
} from "./homeTabs.logic";

export function HomeTabBarLists({ data }: { readonly data: HomeListsData }) {
  const [chosen, setChosen] = useState<HomeTab | null>(null);
  const tab = chosen ?? defaultHomeTab(data.glance);
  const counts = homeTabCounts(data);
  return (
    <TabBar
      items={HOME_TABS.map((id) => ({
        id,
        label: HOME_TAB_LABEL[id],
        count: { value: counts[id], label: homeTabCountLabel(id, counts[id]) },
      }))}
      value={tab}
      onValueChange={setChosen}
      trailing={
        <Link
          to={HOME_TAB_VIEW_ALL[tab]}
          params={{ org: data.orgSegment }}
          className="flex h-8 shrink-0 items-center gap-0.5 rounded-md px-2 text-xs text-muted-foreground outline-none transition-colors duration-[120ms] hover:text-foreground focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/70"
        >
          View all
          <ChevronRightIcon aria-hidden className="size-3.5 stroke-[1.75]" />
        </Link>
      }
      // The bar and its 8px gap sit on top of a 15rem list box, so the rows keep that room.
      className="mt-10 min-h-[11rem] flex-[0_1_17.5rem] gap-2"
    >
      {/* Wider than the column by each row's padding, so row icons and dates sit on the column's edges. */}
      <div className="-mx-2 flex min-h-0 flex-auto flex-col">
        <ScrollArea scrollFade scrollbars="while-scrolling" axis="y" className="min-h-0 flex-auto" data-home-list-scroll="">
          <ScrollAreaContent className="@container/list pb-2">
            <TabsPanel key={tab} value={tab} className={TIMELINE_ROW_ENTER_CLASS} data-home-tab-panel={tab}>
              <HomeTabList tab={tab} data={data} />
            </TabsPanel>
          </ScrollAreaContent>
        </ScrollArea>
      </div>
    </TabBar>
  );
}
